import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

/**
 * `matchMedia` reativo — para o caso em que as duas variações de um layout não
 * podem simplesmente coexistir escondidas por CSS.
 *
 * O modal "Enviar registro" é o exemplo: o painel do anexo aparece como coluna
 * no desktop e como carrossel no celular, e montar os dois (um `md:hidden` e um
 * `hidden md:block`) renderizaria CADA anexo duas vezes — dois object URLs e,
 * num PDF, duas renderizações de canvas por arquivo. Com o `v-if` ligado neste
 * hook, só o layout visível existe.
 *
 * O valor é lido já no setup: sem isso o 1º render sai no layout do `fallback` e
 * troca no mount (o dashboard do celular piscaria o cabeçalho de desktop).
 *
 * Sem `matchMedia` (jsdom dos testes, SSR) vale o `fallback`, `true` por padrão: o
 * desktop é o layout completo, então é ele que um ambiente sem viewport deve
 * descrever. Queries de "celular deitado" passam `false`.
 */
export function useMediaQuery(query: string, fallback = true): Ref<boolean> {
  const mediaQuery =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query)
      : null
  const matches = ref(mediaQuery ? mediaQuery.matches : fallback)

  function update(event: MediaQueryList | MediaQueryListEvent): void {
    matches.value = event.matches
  }

  onMounted(() => {
    if (!mediaQuery) return
    update(mediaQuery)
    // Safari < 14 só tem o `addListener` depreciado; o operador opcional cobre os dois.
    mediaQuery.addEventListener?.('change', update)
  })

  onBeforeUnmount(() => {
    mediaQuery?.removeEventListener?.('change', update)
  })

  return matches
}

/** Quebra `md` do Tailwind — a mesma que separa as colunas do modal. */
export const DESKTOP_MEDIA_QUERY = '(min-width: 768px)'
